import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Truck, CheckCircle2, AlertTriangle } from "@/components/icons";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { MediaUpload } from "@/components/MediaUpload";
import { KnownSolutions } from "@/components/KnownSolutions";
import { Plaatkop } from "@/components/Plaatkop";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";

// Public, no-login fault report reachable via a QR sticker at /m/:qrId.
// UX rule: known solutions must be scannable in seconds, and the report button
// is never more than one tap away. When in doubt: faster to report.
export default function PublicReport() {
  const { qrId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // '404' | 'network' | null
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [laneDown, setLaneDown] = useState(null); // null until chosen
  const [media, setMedia] = useState([]);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [kbArticleId, setKbArticleId] = useState(null);
  const [kbArticleTitle, setKbArticleTitle] = useState("");
  const [feedbackGiven, setFeedbackGiven] = useState({}); // { [articleId]: 'yes'|'no' }
  const [mediaStatus, setMediaStatus] = useState({ uploading: false, failedCount: 0 });
  const [failedConfirmOpen, setFailedConfirmOpen] = useState(false);
  const formRef = useRef(null);

  const loadContext = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await publicApi.get(`/public/report-context/${qrId}`);
      setCtx(data);
    } catch (e) {
      // A real 404 means the sticker is unknown; anything else (a wash hall
      // with bad reception) is a connection problem the reporter can retry.
      setLoadError(e?.response?.status === 404 ? "404" : "network");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadContext(); /* eslint-disable-next-line */ }, [qrId]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const helpful = async (article) => {
    setFeedbackGiven((s) => ({ ...s, [article.id]: "yes" }));
    try { await publicApi.post(`/public/kb/${article.id}/feedback`, { helpful: true }); } catch (e) { /* silent */ }
    toast.success("Fijn! Bedankt voor je antwoord.");
  };

  const notHelpful = async (article) => {
    setFeedbackGiven((s) => ({ ...s, [article.id]: "no" }));
    try { await publicApi.post(`/public/kb/${article.id}/feedback`, { helpful: false }); } catch (e) { /* silent */ }
    // Straight into the report flow, remembering which article did not help.
    setKbArticleId(article.id);
    setKbArticleTitle(article.title);
    scrollToForm();
  };

  const submit = () => {
    setError("");
    if (laneDown === null) { setError("Beantwoord de vraag: ligt er een baan stil?"); return; }
    if (mediaStatus.uploading) { setError("Wacht tot de foto klaar is met uploaden."); return; }
    // A photo that never uploaded would silently go missing — ask first.
    if (mediaStatus.failedCount > 0) { setFailedConfirmOpen(true); return; }
    doSubmit();
  };

  const doSubmit = async () => {
    setFailedConfirmOpen(false);
    if (submitting) return; // guard against double taps
    setSubmitting(true);
    try {
      const { data } = await publicApi.post("/public/faults", {
        qr_id: qrId,
        reporter_name: name,
        description,
        lane_down: laneDown,
        media,
        kb_article_id: kbArticleId,
        website,
      });
      setDone(data);
    } catch (e) {
      setError(getErrorMessage(e, "Melden mislukt. Probeer het later opnieuw."));
    } finally { setSubmitting(false); }
  };

  const solutions = ctx?.known_solutions || [];

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground"><Truck size={18} /></div>
          <div className="leading-tight"><div className="text-body font-semibold">TD-Ops</div><div className="text-data text-muted-foreground">Storing melden</div></div>
        </div>

        {loading && <Card className="p-6 text-center text-body text-muted-foreground">Laden...</Card>}

        {loadError === "404" && !loading && (
          <Card className="p-6 text-center" data-testid="public-not-found">
            <AlertTriangle className="mx-auto mb-2 text-letop-tekst" />
            <p className="text-body text-foreground">Deze sticker is niet bekend — meld het bij je leidinggevende.</p>
          </Card>
        )}

        {loadError === "network" && !loading && (
          <Card className="p-6 text-center" data-testid="public-network-error">
            <AlertTriangle className="mx-auto mb-2 text-letop-tekst" />
            <p className="text-body text-foreground">Geen verbinding. Probeer het zo nog eens.</p>
            <Button className="mt-3" variant="outline" data-testid="public-retry" onClick={loadContext}>Opnieuw proberen</Button>
          </Card>
        )}

        {done && (
          <Card className="p-6 text-center" data-testid="public-report-done">
            <CheckCircle2 className="mx-auto mb-2 text-goed-tekst" size={32} />
            <h2 className="text-body font-semibold text-foreground">Bedankt voor je melding!</h2>
            {done.fault_number && <p className="mt-1 font-mono text-body text-muted-foreground">{done.fault_number}</p>}
            <p className="mt-3 text-body text-muted-foreground">Volg de status via onderstaande link:</p>
            <Link data-testid="public-follow-link" className="mt-1 inline-block break-all text-body text-primary hover:underline" to={`/f/${done.follow_token}`}>
              Status volgen
            </Link>
          </Card>
        )}

        {ctx && !done && !loadError && (
          <div className="space-y-4">
            {/* The same plate the TD sees, so the machine on screen is the machine in front of you. */}
            <Plaatkop
              testid="public-plaatkop"
              code={ctx.asset_code || ctx.location_name}
              subtitle={ctx.asset_code
                ? [ctx.asset_label, ctx.location_name, ctx.ruimte_name, ctx.spoor].filter(Boolean).join(" · ")
                : [ctx.ruimte_name, ctx.location_name].filter(Boolean).join(" · ") || "Locatiesticker"}
            />
            {/* Known solutions come first — scannable, report always one tap away.
                Same component the internal preview renders, so they cannot drift. */}
            <KnownSolutions
              solutions={solutions}
              feedbackGiven={feedbackGiven}
              onHelpful={helpful}
              onNotHelpful={notHelpful}
              onJumpToReport={scrollToForm}
            />

            <Card ref={formRef} className="scroll-mt-4 space-y-4 p-5" data-testid="public-report-form">

              {kbArticleTitle && (
                <div className="rounded-md border border-border bg-muted p-2 text-data text-foreground" data-testid="kb-reference-note">
                  Je gaf aan dat “{kbArticleTitle}” niet hielp. Dit voegen we toe aan je melding.
                </div>
              )}

              {/* Priority question */}
              <div>
                <Label className="mb-2 block">Ligt er een baan stil?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant={laneDown === true ? "default" : "outline"} data-testid="lane-down-yes" onClick={() => setLaneDown(true)}>Ja</Button>
                  <Button type="button" variant={laneDown === false ? "default" : "outline"} data-testid="lane-down-no" onClick={() => setLaneDown(false)}>Nee</Button>
                </div>
                {laneDown === true && <p className="mt-2 text-data text-letop-tekst">Dit wordt als kritiek (P1) gemeld.</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="naam">Je naam</Label>
                <Input id="naam" data-testid="public-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bijv. chauffeur / medewerker" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="omschrijving">Wat is er aan de hand?</Label>
                <Textarea id="omschrijving" data-testid="public-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Korte omschrijving" />
              </div>

              <div className="space-y-1.5">
                <Label>Foto of video (optioneel)</Label>
                <MediaUpload value={media} onChange={setMedia} apiClient={publicApi} uploadPath="/public/storage/upload" onStatusChange={setMediaStatus} />
              </div>

              {/* Honeypot: hidden from humans; bots tend to fill it. */}
              <div className="hidden" aria-hidden="true">
                <label>Website<input tabIndex={-1} autoComplete="off" data-testid="hp-website" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
              </div>

              {error && <p className="text-body text-destructive" data-testid="public-report-error">{error}</p>}
              <Button className="w-full" data-testid="public-submit-button" onClick={submit} disabled={submitting || mediaStatus.uploading}>
                {mediaStatus.uploading ? "Foto wordt geüpload…" : submitting ? "Versturen..." : "Storing melden"}
              </Button>
            </Card>
          </div>
        )}
      </div>

      <AlertDialog open={failedConfirmOpen} onOpenChange={setFailedConfirmOpen}>
        <AlertDialogContent data-testid="public-failed-photo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eén foto is niet meegestuurd</AlertDialogTitle>
            <AlertDialogDescription>
              Er staat nog een foto die niet is geüpload. Wil je de melding toch versturen zonder die foto?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="public-failed-photo-cancel">Terug</AlertDialogCancel>
            <AlertDialogAction data-testid="public-failed-photo-confirm" onClick={doSubmit}>Toch versturen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
